import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Check your email for reset instructions');
    } catch (err) {
      console.error('Failed to send reset email', err);
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Crafters Market
            </h1>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white border border-stone-200 rounded-sm p-8">
          <Link to="/login" className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>

          {!sent ? (
            <>
              <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Forgot Password?
              </h2>
              <p className="text-stone-500 text-sm mb-6">
                Enter your email and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide"
                  data-testid="reset-btn"
                >
                  {loading ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Check Your Email
                </h2>
                <p className="text-stone-500 text-sm mb-6">
                  We've sent password reset instructions to <strong>{email}</strong>
                </p>
                <p className="text-stone-400 text-xs mb-6">
                  Don't see it? Check your spam folder. The link expires in 1 hour.
                </p>
              </div>

              <Button
                onClick={() => setSent(false)}
                variant="outline"
                className="w-full"
              >
                Try another email
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
